import type { PassioClient, PassioResult, RequestOptions } from "./client";
import type {
  AdvisorIncomingVisionRequest,
  AffiliateLinkResponse,
  DailyReviewResult,
  FoodConditionCheckResult,
  FoodTagCatalog,
  GenericFoodRating,
  GlycemicEstimations,
  HumanizedShoppingList,
  IngredientExtractArguments,
  IngredientExtractOutput,
  IngredientSubstitutesResult,
  KitchenToolRequest,
  MealLogAction,
  MealPlanExecution,
  MealPlanResult,
  MealPlanRunInput,
  MealPlanType,
  MenuCoachResult,
  NewPassioFood,
  NutritionAdvisorResponse,
  OutgoingMealResult,
  OutgoingSearchResults,
  PantryRecipeResult,
  PassioConcernCode,
  PassioFood,
  PassioFoodResults,
  QualityScore,
  RecipeMakeoverResult,
  RecreatedDishResult,
  RequestFeedbackDetail,
  RequestFeedbackItem,
  ShoppingList,
  SmartSuggestionsRequest,
  UltraProcessedFoodRating,
} from "./models";
import { normalizeConcernCodes, normalizeSearchResults } from "./normalize";

type CallOptions = Omit<RequestOptions, "query" | "body">;
const seg = encodeURIComponent;
const searchResult = async (p: Promise<PassioResult<OutgoingSearchResults>>) => {
  const res = await p;
  return { ...res, data: normalizeSearchResults(res.data) };
};

abstract class Resource {
  constructor(protected readonly client: PassioClient) {}
}

export interface AdvancedSearchParams {
  term: string;
  limit?: number;
  resultsPerPage?: number;
  preferCatalogHash?: string;
  alternatesOnly?: boolean;
  enrich?: boolean;
  nutrientId?: number[];
}

/** Search, lookups and reference data under `/napi/food`. */
export class FoodsResource extends Resource {
  semanticSearch(term: string, params: { limit?: number } = {}, opts?: CallOptions): Promise<PassioResult<OutgoingSearchResults>> {
    return searchResult(this.client.request("GET", "/napi/food/search/semantic", { ...opts, query: { term, limit: params.limit } }));
  }

  advancedSearch(params: AdvancedSearchParams, opts?: CallOptions): Promise<PassioResult<OutgoingSearchResults>> {
    return searchResult(this.client.request("GET", "/napi/food/search/advanced", { ...opts, query: { ...params } }));
  }

  /** Hydrate a search result (or extracted ingredient) into full nutrition. */
  byRefCode(refCode: string, opts?: CallOptions): Promise<PassioResult<PassioFoodResults>> {
    return this.client.request("GET", `/napi/food/search/result/refCode/${seg(refCode)}`, opts);
  }

  byProductCode(productCode: string, params: { minQualityScore?: QualityScore } = {}, opts?: CallOptions): Promise<PassioResult<PassioFood>> {
    return this.client.request("GET", `/napi/food/productCode/${seg(productCode)}`, { ...opts, query: params });
  }

  scanDefaults(passioID: string, opts?: CallOptions): Promise<PassioResult<PassioFoodResults>> {
    return this.client.request("GET", "/napi/food/scan/defaults", { ...opts, query: { passioID } });
  }

  tags(opts?: CallOptions): Promise<PassioResult<FoodTagCatalog>> {
    return this.client.request("GET", "/napi/food/tags", opts);
  }

  async concernCodes(opts?: CallOptions): Promise<PassioResult<PassioConcernCode[]>> {
    const res = await this.client.request<unknown>("GET", "/napi/food/concernCodes", opts);
    return { ...res, data: normalizeConcernCodes(res.data) };
  }

  /** Match a list of structured ingredients to database foods in one call. */
  linkIngredients(items: IngredientExtractArguments[], opts?: CallOptions): Promise<PassioResult<OutgoingSearchResults>> {
    return searchResult(this.client.request("POST", "/napi/food/search/linkIngredients", { ...opts, body: items }));
  }
}

type ToolInput = string | SmartSuggestionsRequest;
const toolBody = (input: ToolInput, message?: string): SmartSuggestionsRequest =>
  typeof input === "string" ? { content: input, ...(message ? { message } : {}) } : input;

/** Text-based AI tools under `/napi/tools`. Each takes `content` (and an optional `message`). */
export class ToolsResource extends Resource {
  private call<T>(route: string, input: ToolInput, message?: string, opts?: CallOptions) {
    return this.client.request<T>("POST", `/napi/tools/${route}`, { ...opts, body: toolBody(input, message) });
  }

  /** Free text to structured foods, with meal / component grouping when the text describes meals. */
  extractIngredients(input: ToolInput, message?: string, opts?: CallOptions) {
    return this.call<IngredientExtractOutput[]>("extractIngredients", input, message, opts);
  }
  /** "Log two eggs for breakfast, remove the toast" to add/remove actions. */
  extractMealLogAction(input: ToolInput, message?: string, opts?: CallOptions) {
    return this.call<MealLogAction[]>("extractMealLogAction", input, message, opts);
  }
  /** Ingredients a dish probably contains but nobody mentions (oil, butter, salt). */
  invisibleIngredientSuggestions(input: ToolInput, message?: string, opts?: CallOptions) {
    return this.call<IngredientExtractOutput[]>("invisibleIngredientSuggestions", input, message, opts);
  }
  possibleIngredientSuggestions(input: ToolInput, message?: string, opts?: CallOptions) {
    return this.call<IngredientExtractOutput[]>("possibleIngredientSuggestions", input, message, opts);
  }
  predictIngredients(input: ToolInput, message?: string, opts?: CallOptions) {
    return this.call<IngredientExtractOutput[]>("predictIngredients", input, message, opts);
  }
  visualAlternativeSuggestions(input: ToolInput, message?: string, opts?: CallOptions) {
    return this.call<IngredientExtractOutput[]>("visualAlternativeSuggestions", input, message, opts);
  }
  estimateGlycemicIndexLoad(input: ToolInput, message?: string, opts?: CallOptions) {
    return this.call<GlycemicEstimations>("estimateGlycemicIndexLoad", input, message, opts);
  }
  ultraProcessedFoodRating(input: ToolInput, message?: string, opts?: CallOptions) {
    return this.call<UltraProcessedFoodRating>("ultraProcessedFoodRating", input, message, opts);
  }
  nutritionAdvisor(input: ToolInput, message?: string, opts?: CallOptions) {
    return this.call<NutritionAdvisorResponse>("nutritionAdvisor", input, message, opts);
  }
  /** Free text to one structured meal: components (main, side, drink…) each with linked ingredients. */
  extractFoodAndIngredients(input: ToolInput, message?: string, opts?: CallOptions) {
    return this.call<OutgoingMealResult>("extractFoodAndIngredients", input, message, opts);
  }
  /** Rate a food against any criterion: `rateFood("granola bar", "good for a kid's lunchbox?")`. */
  genericFoodRating(food: string, criterion: string, opts?: CallOptions) {
    return this.call<GenericFoodRating>("genericFoodRating", `Food: ${food}\nCriterion: ${criterion}`, undefined, opts);
  }
  /** Raw quantities ("400 g strawberries") to what a shopper buys ("2 containers of strawberries"). */
  humanizeShoppingList(input: ToolInput, message?: string, opts?: CallOptions) {
    return this.call<HumanizedShoppingList>("humanizeShoppingList", input, message, opts);
  }
  /** Menu (photo, video, text or audio) to the best picks for the user's goals, with ordering tips and items to avoid. */
  menuCoach(req: KitchenToolRequest, opts?: CallOptions) {
    return kitchenCall<MenuCoachResult>(this.client, "menuCoach", req, opts);
  }
  /** How well a food or meal fits `conditions` ("type 2 diabetes", "GERD"). General information, not medical advice. */
  foodConditionCheck(req: KitchenToolRequest, opts?: CallOptions) {
    return kitchenCall<FoodConditionCheckResult>(this.client, "foodConditionCheck", req, opts);
  }
  /** A day's `dayLog` against `preferences.targets`, with three next-meal ideas for what's left. No video. */
  dailyReview(req: KitchenToolRequest, opts?: CallOptions) {
    return kitchenCall<DailyReviewResult>(this.client, "dailyReview", req, opts);
  }
  /** Start a meal plan. Returns at once with the run `id`; poll `mealPlanExecution(id)` or use `runMealPlan`. */
  startMealPlan(type: MealPlanType, input: MealPlanRunInput, opts?: CallOptions) {
    return this.client.request<MealPlanExecution>("POST", `/napi/tools/mealPlans/${type}`, { ...opts, body: { input_data: input } });
  }
  /** A meal plan run. Once `completed` it has `meal_plan` (days, macro targets, PDF) and `platform_billed_tokens`. */
  mealPlanExecution(executionId: string, opts?: CallOptions) {
    return this.client.request<MealPlanExecution>("GET", `/napi/tools/mealPlans/executions/${seg(executionId)}`, opts);
  }
  /** Start a meal plan and poll every `intervalMs` until the run ends (`completed`, `failed` or `cancelled`; plans take a few minutes). */
  async runMealPlan(type: MealPlanType, input: MealPlanRunInput, opts: CallOptions & { intervalMs?: number; maxPolls?: number } = {}) {
    const { intervalMs = 3000, maxPolls = 200, ...call } = opts;
    const id = (await this.startMealPlan(type, input, call)).data.id;
    if (!id) throw new Error("The meal plan run didn't return an id.");
    for (let i = 0; i < maxPolls; i++) {
      await wait(intervalMs, call.signal);
      const res = await this.mealPlanExecution(id, call);
      if (["completed", "failed", "cancelled"].includes(String(res.data.status))) return res;
    }
    throw new Error(`Meal plan ${id} is still running after ${maxPolls} polls.`);
  }
}

const wait = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) return reject(signal.reason);
    const t = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(t);
        reject(signal.reason);
      },
      { once: true },
    );
  });

/** Kitchen and health tools take text and/or one media data URL (`image`, `video` or `audio`) plus details. Audio and video need a Gemini model. */
const kitchenCall = <T>(client: PassioClient, route: string, req: KitchenToolRequest, opts?: CallOptions) =>
  client.request<T>("POST", `/napi/tools/${route}`, { ...opts, body: req });

/** Recipe tools under `/napi/tools/recipes`. Recipe ingredients come back linked to Passio foods. */
export class RecipesResource extends Resource {
  /**
   * "What can I cook?": a fridge or pantry (photo, video, voice or text) plus preferences to one recipe and
   * alternate meal titles. To make an alternate, resend with `selectedMeal` and `ingredients` (the earlier
   * `detectedIngredients` names) instead of the media.
   */
  fromPantry(req: KitchenToolRequest, opts?: CallOptions) {
    return kitchenCall<PantryRecipeResult>(this.client, "recipes/fromPantry", req, opts);
  }
  /** A dish (photo, video or description) to a home recipe for it, plus similar dish titles. */
  recreateDish(req: KitchenToolRequest, opts?: CallOptions) {
    return kitchenCall<RecreatedDishResult>(this.client, "recipes/recreateDish", req, opts);
  }
  /** Rewrite a recipe to meet `goal` ("high protein", "lower sodium"), listing each swap and before/after nutrition. */
  makeover(req: KitchenToolRequest, opts?: CallOptions) {
    return kitchenCall<RecipeMakeoverResult>(this.client, "recipes/makeover", req, opts);
  }
  /** Ranked substitutes with ratios for `ingredients` (or ingredients named in `text`). No video. */
  substitutes(req: KitchenToolRequest, opts?: CallOptions) {
    return kitchenCall<IngredientSubstitutesResult>(this.client, "recipes/substitutes", req, opts);
  }
}

/** Image tools. `image` is a data URI (`data:image/jpeg;base64,...`). */
export class VisionResource extends Resource {
  /** Meal photo to typed foods (with meal / component structure). */
  extractIngredientsAutoTyped(req: AdvisorIncomingVisionRequest, opts?: CallOptions): Promise<PassioResult<IngredientExtractOutput[]>> {
    return this.client.request("POST", "/napi/tools/vision/extractIngredientsAutoTyped", { ...opts, body: req });
  }
  /** Package / nutrition-label photo to a food with nutrients. */
  extractPackagedProduct(req: AdvisorIncomingVisionRequest, opts?: CallOptions): Promise<PassioResult<NewPassioFood>> {
    return this.client.request("POST", "/napi/tools/vision/extractPackagedProduct", { ...opts, body: req });
  }
  /** Plate / dish photo to linked ingredients, skipping auto-typing (no packaged-product handling). */
  extractIngredients(req: AdvisorIncomingVisionRequest, opts?: CallOptions): Promise<PassioResult<IngredientExtractOutput[]>> {
    return this.client.request("POST", "/napi/tools/vision/extractIngredients", { ...opts, body: req });
  }
  /** Multi-component plate photo to one structured meal (components with linked ingredients). */
  extractFoodAndIngredients(req: AdvisorIncomingVisionRequest, opts?: CallOptions): Promise<PassioResult<OutgoingMealResult>> {
    return this.client.request("POST", "/napi/tools/vision/extractFoodAndIngredients", { ...opts, body: req });
  }
}

export interface AffiliateAttribution {
  customerId?: string;
  sourceTool?: string;
  cartId?: string;
  campaign?: string;
  title?: string;
  imageUrl?: string;
}

export class AffiliatesResource extends Resource {
  /** A shopping list (or a meal-plan result) to a tracked Instacart cart link. */
  convertShoppingList(
    list: ShoppingList | MealPlanResult,
    attribution: AffiliateAttribution = {},
    opts?: CallOptions,
  ): Promise<PassioResult<AffiliateLinkResponse>> {
    return this.client.request("POST", "/napi/affiliates/convertShoppingListToAffiliateLink", {
      ...opts,
      body: list,
      query: { ...attribution },
    });
  }
}

export class FeedbackResource extends Resource {
  /** Report what the user did with an earlier result (`requestId` = its `X-Request-ID`). */
  send(items: RequestFeedbackItem | RequestFeedbackItem[], opts?: CallOptions): Promise<PassioResult<unknown>> {
    return this.client.request("POST", "/napi/feedback", { ...opts, body: { items: Array.isArray(items) ? items : [items] } });
  }
  /** What Passio recorded for one `X-Request-ID` (route, model, tokens, cost, Easy AI runs) and the feedback sent for it. */
  lookup(requestId: string, opts?: CallOptions): Promise<PassioResult<RequestFeedbackDetail>> {
    return this.client.request("GET", `/napi/feedback/${seg(requestId)}`, opts);
  }
}
